import { MutableRefObject } from 'react';
import { Translate } from 'next-translate';

import { CustomColumnHeader, ID, Lot } from 'types';

import { getHeaderID, isCustomColumn, isVisibleToSuppliers } from './utils';

interface CanRemoveColumnParams {
  headerMap: ID[];
  headers?: CustomColumnHeader[];
  hotRef: MutableRefObject<any>;
  isReadOnly: boolean;
}

interface GetDisplayTextParams {
  id?: ID;
  lot: Lot;
}

export interface ColumnToDelete {
  id: ID;
  title: string;
}

interface OnRemoveColumnParams {
  headerMap: ID[];
  headers?: CustomColumnHeader[];
  hotRef: MutableRefObject<any>;
  isReadOnly: boolean;
  lot: Lot;
  setColumnToDelete: (record: ColumnToDelete) => void;
  t: Translate;
}

export const titleToDelete = ({ displayText, lotName, t }: {
  displayText?: string;
  lotName: string;
  t: Translate;
}): string => {
  let title = '';

  if (lotName?.length) title += `[${lotName}]`;
  title += ` ${t('general.column')}`;
  if (displayText?.length) title += ` [${displayText}]`;

  return title;
};

export const canRemoveColumn = ({
  headerMap,
  headers,
  hotRef,
  isReadOnly,
}: CanRemoveColumnParams): boolean => {
  const headerId = getHeaderID({ headerMap, hotRef });

  if (!headerId || (isReadOnly && isVisibleToSuppliers(headerId, headers))) return false;

  return isCustomColumn(headerId);
};

const getDisplayText = ({ id, lot, }: GetDisplayTextParams): string | undefined => {
  if (!id) return undefined;

  return lot?.
    custom_column_headers?.
    find((header: CustomColumnHeader): boolean => (
      header.id === id
    ))?.display_text || String(id);
};

export const onRemoveColumn = ({
  headerMap,
  headers,
  hotRef,
  lot,
  setColumnToDelete,
  t,
  isReadOnly,
}: OnRemoveColumnParams): void => {
  const headerId = getHeaderID({ headerMap, hotRef });

  if (!headerId || (isReadOnly && isVisibleToSuppliers(headerId, headers))) return;
  const displayText = getDisplayText({ id: headerId, lot });

  if (!displayText?.length) return;
  const title = titleToDelete({ displayText, lotName: lot.name, t });

  setColumnToDelete({ id: headerId, title });
};

interface GetRemoveColumnNameParams {
  headerMap: ID[];
  headers?: CustomColumnHeader[];
  hotRef: MutableRefObject<any>;
  isReadOnly: boolean;
  t: Translate;
}

export const getRemoveColumnName = ({
  headerMap,
  headers,
  hotRef,
  isReadOnly,
  t,
}: GetRemoveColumnNameParams): string => {
  const headerId = getHeaderID({ headerMap, hotRef });

  if (!headerId || !isReadOnly || isVisibleToSuppliers(headerId, headers)) return t('bid_request.line_items.dropdown.remove_column');

  return t('bid_request.line_items.dropdown.remove_hidden_column');
};
